import React from 'react';
import { Document, Page as RPDFPage, View, Text, StyleSheet } from '@react-pdf/renderer';
import type { PlatformInvoicePrint } from '../../../services/billingService';
import { Footer, Pill, pdfColors, pdfFontFamily, pdfLetter, pdfPageFrame, fmtMoney } from '../../../utils/pdf';

interface Props {
  invoice: PlatformInvoicePrint;
  generatedAt: string;
}

/** EduBook's own blue — this invoice is from EduBook to the school, not the school's letterhead. */
const BRAND = '#1976d2';
const BRAND_DEEP = '#1e3a8a';

const label = { fontSize: 7, fontWeight: 700, textTransform: 'uppercase', letterSpacing: pdfLetter.openStrong, color: pdfColors.textMuted } as const;

const styles = StyleSheet.create({
  page: {
    paddingTop: 46, paddingBottom: pdfPageFrame.contentBottom, paddingHorizontal: 48,
    fontFamily: pdfFontFamily.sans, fontSize: 9.5, color: pdfColors.textPrimary, backgroundColor: pdfColors.surface,
  },
  band: { position: 'absolute', top: 0, left: 0, right: 0, height: 5, backgroundColor: BRAND },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' },
  brand: { fontSize: 22, fontWeight: 700, color: BRAND, letterSpacing: -0.3 },
  tagline: { fontSize: 9, color: pdfColors.textMuted, marginTop: 3 },
  titleBox: { alignItems: 'flex-end' },
  title: { fontSize: 26, fontWeight: 700, color: pdfColors.textPrimary, letterSpacing: 2 },
  number: { fontFamily: pdfFontFamily.mono, fontSize: 9.5, color: pdfColors.textSecondary, marginTop: 4 },
  rule: { height: 1.6, backgroundColor: BRAND, marginTop: 18, marginBottom: 22 },
  parties: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 24 },
  billTo: { flex: 1, paddingRight: 24 },
  billLabel: { ...label, marginBottom: 6 },
  billName: { fontSize: 13, fontWeight: 700 },
  billLine: { fontSize: 9, color: pdfColors.textSecondary, marginTop: 3 },
  meta: {
    width: 232, paddingVertical: 10, paddingHorizontal: 14, borderRadius: 6,
    backgroundColor: pdfColors.surfaceSoft, borderWidth: 0.6, borderColor: pdfColors.divider, borderStyle: 'solid',
  },
  metaRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 3.5 },
  metaLabel: { fontSize: 9, color: pdfColors.textMuted },
  metaValue: { fontSize: 9, fontWeight: 600 },
  table: { borderRadius: 6, borderWidth: 0.6, borderColor: pdfColors.divider, borderStyle: 'solid', overflow: 'hidden' },
  thead: { flexDirection: 'row', backgroundColor: BRAND_DEEP, paddingVertical: 8, paddingHorizontal: 14 },
  th: { fontSize: 7.5, fontWeight: 700, color: pdfColors.textOnDark, textTransform: 'uppercase', letterSpacing: pdfLetter.openStrong },
  tbody: { flexDirection: 'row', alignItems: 'center', paddingVertical: 14, paddingHorizontal: 14 },
  item: { fontSize: 11, fontWeight: 700 },
  itemSub: { fontSize: 8.5, color: pdfColors.textMuted, marginTop: 3 },
  cycle: { fontSize: 9.5, color: pdfColors.textSecondary },
  amount: { fontSize: 10.5, fontWeight: 600, textAlign: 'right' },
  colItem: { flex: 1 },
  colCycle: { width: 110 },
  colAmount: { width: 120, textAlign: 'right' },
  totals: { alignSelf: 'flex-end', width: 250, marginTop: 16, borderRadius: 6, overflow: 'hidden', borderWidth: 0.6, borderColor: pdfColors.divider, borderStyle: 'solid' },
  totalRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 12, paddingHorizontal: 14, backgroundColor: '#eff6ff', borderTopWidth: 2, borderTopColor: BRAND, borderTopStyle: 'solid' },
  totalLabel: { fontSize: 10.5, fontWeight: 600 },
  totalValue: { fontSize: 17, fontWeight: 700, color: BRAND },
  details: { marginTop: 24, paddingTop: 12, borderTopWidth: 0.6, borderTopColor: pdfColors.divider, borderTopStyle: 'solid' },
  detailsTitle: { ...label, marginBottom: 8 },
  detailRow: { flexDirection: 'row', paddingVertical: 2.5 },
  detailLabel: { width: 90, fontSize: 9, color: pdfColors.textMuted },
  detailValue: { flex: 1, fontSize: 9, fontWeight: 500 },
  thanks: { marginTop: 40, alignItems: 'center' },
  thanksText: { fontSize: 10, color: pdfColors.textSecondary },
  thanksBrand: { fontSize: 9.5, fontWeight: 600, color: BRAND, marginTop: 4 },
});

/**
 * A subscription invoice from EduBook to a school: the EduBook masthead and
 * invoice number, who is billed and when it was paid, the plan, the total in
 * a highlighted box, and how it was paid.
 */
const PlatformInvoiceDocument: React.FC<Props> = ({ invoice: i, generatedAt }) => {
  const money = (n: number) => fmtMoney(n, i.currency);
  const details = [
    i.reference && ['Reference', i.reference],
    i.notes && ['Notes', i.notes],
    ['Received by', i.receivedBy],
  ].filter(Boolean) as Array<[string, string]>;

  return (
    <Document title={`Invoice ${i.invoiceNumber}`} author="EduBook">
      <RPDFPage size="A4" style={styles.page}>
        <View style={styles.band} fixed />
        <View style={styles.header}>
          <View>
            <Text style={styles.brand}>EduBook</Text>
            <Text style={styles.tagline}>School Management System</Text>
          </View>
          <View style={styles.titleBox}>
            <Text style={styles.title}>INVOICE</Text>
            <Text style={styles.number}>#{i.invoiceNumber}</Text>
          </View>
        </View>
        <View style={styles.rule} />

        <View style={styles.parties} wrap={false}>
          <View style={styles.billTo}>
            <Text style={styles.billLabel}>Bill to</Text>
            <Text style={styles.billName}>{i.billTo.name}</Text>
            {[i.billTo.address, i.billTo.email, i.billTo.phone].filter(Boolean).map(l => <Text key={l!} style={styles.billLine}>{l}</Text>)}
          </View>
          <View style={styles.meta}>
            <View style={styles.metaRow}><Text style={styles.metaLabel}>Invoice date</Text><Text style={styles.metaValue}>{i.date}</Text></View>
            <View style={styles.metaRow}><Text style={styles.metaLabel}>Payment method</Text><Text style={styles.metaValue}>{i.method}</Text></View>
            <View style={styles.metaRow}><Text style={styles.metaLabel}>Status</Text><Pill dense label="Paid" tone="success" /></View>
          </View>
        </View>

        <View style={styles.table} wrap={false}>
          <View style={styles.thead}>
            <Text style={[styles.th, styles.colItem]}>Description</Text>
            <Text style={[styles.th, styles.colCycle]}>Billing cycle</Text>
            <Text style={[styles.th, styles.colAmount]}>Amount</Text>
          </View>
          <View style={styles.tbody}>
            <View style={styles.colItem}>
              <Text style={styles.item}>{i.planName} Plan Subscription</Text>
              <Text style={styles.itemSub}>Complete access to all features and modules</Text>
            </View>
            <Text style={[styles.cycle, styles.colCycle]}>{i.billingCycle}</Text>
            <Text style={[styles.amount, styles.colAmount]}>{money(i.amount)}</Text>
          </View>
        </View>

        <View style={styles.totals} wrap={false}>
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>Total amount</Text>
            <Text style={styles.totalValue}>{money(i.amount)}</Text>
          </View>
        </View>

        <View style={styles.details} wrap={false}>
          <Text style={styles.detailsTitle}>Payment details</Text>
          {details.map(([k, v]) => (
            <View key={k} style={styles.detailRow}>
              <Text style={styles.detailLabel}>{k}</Text>
              <Text style={styles.detailValue}>{v}</Text>
            </View>
          ))}
        </View>

        <View style={styles.thanks} wrap={false}>
          <Text style={styles.thanksText}>Thank you for your business!</Text>
          <Text style={styles.thanksBrand}>EduBook School Management System</Text>
        </View>

        <Footer meta={{ title: 'Invoice', generatedAt }} />
      </RPDFPage>
    </Document>
  );
};

export default PlatformInvoiceDocument;
